import * as app from 'CMS/core/application';
import * as $ from 'jquery';
import PushMessageSubscription from "../Data/Entities/PushMessageSubscription";
import PushMessageSubscriptionRepository from "../Data/Repositories/PushMessageSubscriptionRepository";
import { PushSubscriptionType } from "./Enums";

export namespace WebPushHelper {
    export const WEBPUSHENABLED: string = 'webPushEnabled';
    export const WEBPUSHSUBSCRIPTIONENDPOINT: string = 'webPushSubscriptionEnpoint';
    export const WEBPUSHAPPLICATIONSERVERKEY: string = 'webPushApplicationServerKey';
    export const WEBPUSHUSERID: string = 'webPushUserId';


    export function disableNotificationCheckboxes(disabled: boolean) {
        const checkboxes = $('.notification-settings .notifications-checkbox');
        for (let i = 0; i < checkboxes.length; i++) {
            (checkboxes[i] as HTMLInputElement).disabled = disabled;
        }
    }

    export function setNotificationCheckboxes(checked: boolean) {
        const checkboxes = $('.notification-settings .notifications-checkbox');
        for (let i = 0; i < checkboxes.length; i++) {
            (checkboxes[i] as HTMLInputElement).checked = checked;
        }
    }

    export function setNotificationBlockedTextVisibility(visible: boolean) {
        const blockedTexts = $('.notification-settings .notifications-blocked-wrapper');
        if (visible) {
            $.each(blockedTexts, (_, txt) => {
                txt.classList.remove('hidden');
            });
        } else {
            $.each(blockedTexts, (_, txt) => {
                txt.classList.add('hidden');
            });
        }
    }

    export function getLocalStorageValue(key: string): string {
        return localStorage.getItem(key);
    }

    export function setLocalStorageValue(key: string, value: string) {
        localStorage.setItem(key, value);
    }

    export function clearLocalStorageValue(key: string) {
        localStorage.removeItem(key);
    }

    /**
     * Check current permission and subscription and subscribe if needed
     * 
     */
    export async function initializeSubscription() {
        let currentUser: IAWFUser = null;
        try {
            currentUser = app.getCurrentUser();
        } catch {
            // awfConfig not yet initialized
            currentUser = (window as any).awfConfig.g.cu;
        }
        if (!currentUser.loggedIn) {
            return;
        }

        disableNotificationCheckboxes(true);
        const serviceWorkerRegistration = await navigator.serviceWorker.ready;
        const subscription = await serviceWorkerRegistration.pushManager.getSubscription();
        if (subscription) {
            // active subscription
            await handleActiveSubscription(subscription, currentUser);
        } else {
            // no active subscription
            await handleInactiveSubscription();
        }
        disableNotificationCheckboxes(false);
    }

    /**
     * Handles the flow of having active subscription in the pushManager
     * @param subscription
     * @param currentUser
     */
    async function handleActiveSubscription(subscription: PushSubscription, currentUser: IAWFUser) {
        // check if the current logged in user is the same as the subscription user
        const webPushUserId = getLocalStorageValue(WEBPUSHUSERID);
        if (getLocalStorageValue(WEBPUSHENABLED) === 'true') {
            if (currentUser.id === webPushUserId.replaceAll('-', '')) {
                // update subscription
                const subscriptionJson = JSON.parse(JSON.stringify(subscription));
                const subscriptionToSave = new PushMessageSubscription();
                subscriptionToSave.deviceType = 10 //BE.FlaRIS.VisuRIS.Business.AlertPushChannelDeviceType.Website
                subscriptionToSave.application = 2; //BE.FlaRIS.VisuRIS.Business.ApplicationVisibility.Website
                subscriptionToSave.enabled = true;
                subscriptionToSave.pushSubscriptionType = PushSubscriptionType.Vapid;
                subscriptionToSave.endpoint = subscriptionJson.endpoint;
                subscriptionToSave.auth = subscriptionJson.keys.auth;
                subscriptionToSave.p256dh = subscriptionJson.keys.p256dh;
                const repository = new PushMessageSubscriptionRepository();
                const savedSub = await repository.savePushMessageSubscription(subscriptionToSave);
                setLocalStorageValue(WEBPUSHSUBSCRIPTIONENDPOINT, savedSub.endpoint);
            } else {
                // unsubscribe and delete old subscription
                await unsubscribe();
                // get new subscription for new user
                await subscribe();
            }
            setNotificationCheckboxes(true);
        } else {
            // unsubscribe and delete active subscription
            await unsubscribe();
            setNotificationCheckboxes(false);
        }
    }

    /**
     * Handles the flow inactive subscription
     */
    async function handleInactiveSubscription() {
        // no active subscription, but has enabled webpush before and permission -> checked box and subscribe
        if (getLocalStorageValue(WEBPUSHENABLED) === 'true') {
            if (Notification.permission === 'granted') {
                const subscription = await subscribe();
                // send confirm notification
                await sendConfirmationNotification(subscription);
                setNotificationBlockedTextVisibility(false);
            } else {
                // no permission -> show blocked text
                setNotificationBlockedTextVisibility(true);
            }
            setNotificationCheckboxes(true);
        } else {
            clearLocalStorageValue(WEBPUSHSUBSCRIPTIONENDPOINT);
            clearLocalStorageValue(WEBPUSHUSERID);
            setNotificationCheckboxes(false);
        }
    }

    /**
     * Subscribe using pushmanager, save subscription to DB, save endpoint, userId and enabled locally
     * @returns
     */
    async function subscribe(): Promise<PushMessageSubscription> {
        try {
            const serviceWorkerRegistration = await navigator.serviceWorker.ready;
            const options = {
                userVisibleOnly: true,
                applicationServerKey: getLocalStorageValue(WEBPUSHAPPLICATIONSERVERKEY)
            };
            const subscription = await serviceWorkerRegistration.pushManager.subscribe(options);
            // save subscription to server
            const subscriptionJson = JSON.parse(JSON.stringify(subscription));
            const subscriptionToSave = new PushMessageSubscription();
            subscriptionToSave.deviceType = 10 //BE.FlaRIS.VisuRIS.Business.AlertPushChannelDeviceType.Website
            subscriptionToSave.application = 2; //BE.FlaRIS.VisuRIS.Business.ApplicationVisibility.Website
            subscriptionToSave.enabled = true;
            subscriptionToSave.pushSubscriptionType = PushSubscriptionType.Vapid;
            subscriptionToSave.endpoint = subscriptionJson.endpoint;
            subscriptionToSave.auth = subscriptionJson.keys.auth;
            subscriptionToSave.p256dh = subscriptionJson.keys.p256dh;
            const repository = new PushMessageSubscriptionRepository();
            const savedSub = await repository.savePushMessageSubscription(subscriptionToSave);
            setLocalStorageValue(WEBPUSHSUBSCRIPTIONENDPOINT, subscriptionJson.endpoint);
            setLocalStorageValue(WEBPUSHUSERID, savedSub.userId);
            return savedSub;
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    /**
     * Unsubscribe current subscription using PushManager and delete from DB
     */
    async function unsubscribe() {
        try {
            const serviceWorkerRegistration = await navigator.serviceWorker.ready;
            const subscription = await serviceWorkerRegistration.pushManager.getSubscription();
            await subscription.unsubscribe();
            const repository = new PushMessageSubscriptionRepository();
            await repository.deletePushMessageSubscriptionByEndpoint(subscription.endpoint)
            clearLocalStorageValue(WEBPUSHSUBSCRIPTIONENDPOINT);
            clearLocalStorageValue(WEBPUSHUSERID);
        } catch (e) {
            console.error(e);
        }
    }

    async function sendConfirmationNotification(subscription: PushMessageSubscription) {
        try {
            if (!subscription) {
                return;
            }
            const repository = new PushMessageSubscriptionRepository();
            await repository.sendPermissionGrantedNotification(subscription.endpoint);
        } catch (e) {
            console.error(e);
        }
    }
}
